import { defineStore } from 'pinia'
import type { FolderDTO, Folder } from '~/types/folder'
import type { Document } from '~/types/document'
export const useFolders = defineStore('folders', () => {
  const baseApiURL = useRuntimeConfig().public.apiUrl
  const folders: Ref<Folder[]> = useState(
    'folders:active',
    () => [],
  )
  const loading: Ref<boolean> = useState(
    'folders:loading',
    () => false,
  )
  const folder = ref<Folder | undefined>(undefined)
  const { company } = useCompany()

  const fetchFolderByMachineIdAndUuid = async (
    machineId: number,
    uuid: string,
  ) => {
    loading.value = true

    try {
      folder.value = await $fetch(
        `${baseApiURL}/folders/machine/${machineId}/uuid/${uuid}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      )
      loading.value = false
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  async function createFolder(folderData: FolderDTO) {
    try {
      const newFolder = await $fetch<any>(
        `${baseApiURL}/folders/`,
        {
          method: 'POST',
          credentials: 'include',
          body: {
            ...folderData,
          },
        },
      )

      folders.value.push(newFolder)

      return newFolder
    } catch (e: any) {
      console.error(e)
      throw e
    }
  }

  async function getFoldersByMachineId(
    machineId: number,
  ): Promise<Folder[]> {
    loading.value = true

    try {
      const machineFolders: Folder[] =
        getFolderFromMachine(machineId)

      if (machineFolders.length > 0) {
        return machineFolders
      }

      const newFolders: Folder[] = await $fetch<Folder[]>(
        `${baseApiURL}/folders/machine/${machineId}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      )

      newFolders.forEach((folder) => {
        folders.value?.push(folder)
      })

      return newFolders
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function getFoldersByCompanyId(companyId: number) {
    loading.value = true

    try {
      folders.value = await $fetch(
        `${baseApiURL}/folders/company/${companyId}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      )

      return folders.value
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  async function getFolderByUuid(uuid: string) {
    loading.value = true

    try {
      const respFolder = await $fetch(
        `${baseApiURL}/folders/uuid/${uuid}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      )

      return respFolder
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  async function getFolderQr(
    folder_id: number,
  ): Promise<Folder | undefined> {
    try {
      const folderWithQr = await $fetch<Folder>(
        `${baseApiURL}/folders/${folder_id}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      )

      const index = folders.value.findIndex(
        (f) => f.id === folder_id,
      )

      if (index !== -1) {
        folders.value[index] = folderWithQr
      }

      return folderWithQr
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  async function deleteFolder(folder_id: number) {
    try {
      await $fetch(`${baseApiURL}/folders/${folder_id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      useDocuments().clearDocuments(folder_id)

      const index = folders.value.findIndex(
        (f) => f.id === folder_id,
      )

      if (index !== -1) {
        folders.value.splice(index, 1)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    folders,
    folder,
    loading,

    createFolder,

    getFoldersByMachineId,
    getFoldersByCompanyId,
    getFolderQr,
    getFolderByUuid,
    fetchFolderByMachineIdAndUuid,

    deleteFolder,
  }
})
